import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { Loader2, FileText, Calendar } from 'lucide-react'
import { wordpressApi, type WPPost, type WPCategory } from '../../api/wordpress'

export function SitemapPage() {
  const [posts, setPosts] = useState<WPPost[]>([])
  const [categories, setCategories] = useState<WPCategory[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postsResponse, categoriesData] = await Promise.all([
          wordpressApi.getPosts({ per_page: 100 }),
          wordpressApi.getCategories(),
        ])
        setPosts(postsResponse.posts)
        setCategories(categoriesData)
      } catch (error) {
        console.error('Failed to fetch sitemap data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()

    // Set page title and meta
    document.title = 'Sitemap | The Wedsy Blog'
    const meta = document.querySelector('meta[name="description"]')
    if (meta) {
      meta.setAttribute('content', 'Complete sitemap of all wedding planning blog posts and categories on Wedsy.')
    }
  }, [])

  // Group posts by year
  const postsByYear = posts.reduce((acc, post) => {
    const year = new Date(post.date).getFullYear()
    if (!acc[year]) acc[year] = []
    acc[year].push(post)
    return acc
  }, {} as Record<number, WPPost[]>)

  const years = Object.keys(postsByYear).sort((a, b) => Number(b) - Number(a))

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-text-primary mb-4">Sitemap</h1>
          <p className="text-lg text-text-secondary">
            Browse all {posts.length} wedding planning blog posts organised by year and category.
          </p>
        </div>

        {/* Quick Links */}
        <div className="mb-12 p-6 bg-background-card border border-border rounded-lg">
          <h2 className="text-xl font-semibold text-text-primary mb-4">Quick Links</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h3 className="text-sm font-medium text-text-secondary mb-2">Main Pages</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-primary hover:underline">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-primary hover:underline">
                    Blog
                  </Link>
                </li>
              </ul>
            </div>
            {categories.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-text-secondary mb-2">Categories</h3>
                <ul className="space-y-2">
                  {categories.map(category => (
                    <li key={category.id}>
                      <Link
                        to={`/blog/category/${category.slug}`}
                        className="text-primary hover:underline"
                      >
                        {category.name} ({category.count})
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Posts by Year */}
        <div className="space-y-12">
          {years.map(year => (
            <div key={year}>
              <h2 className="text-2xl font-bold text-text-primary mb-6 pb-2 border-b border-border">
                {year}
              </h2>
              <div className="space-y-4">
                {postsByYear[Number(year)].map(post => (
                  <article
                    key={post.id}
                    className="flex items-start gap-4 p-4 rounded-lg hover:bg-background-elevated transition-colors"
                  >
                    <FileText className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/blog/${post.slug}`}
                        className="text-lg font-medium text-text-primary hover:text-primary transition-colors block mb-1"
                        dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                      />
                      <div className="flex items-center gap-4 text-sm text-text-secondary">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(post.date), 'MMM d, yyyy')}
                        </span>
                        {(post._embedded?.['wp:term']?.[0]?.length ?? 0) > 0 && (
                          <span className="text-primary">
                            {post._embedded?.['wp:term']?.[0]?.[0]?.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* XML Sitemap Link */}
        <div className="mt-12 p-6 bg-background-card border border-border rounded-lg text-center">
          <p className="text-text-secondary mb-3">
            Looking for the XML sitemap for search engines?
          </p>
          <a
            href="/sitemap.xml"
            className="inline-flex items-center gap-2 text-primary hover:underline"
          >
            <FileText className="w-4 h-4" />
            View XML Sitemap
          </a>
        </div>
      </div>
    </div>
  )
}
